<script setup lang="ts">
import {computed} from "vue";
import type {JsonApiResponse} from "~/types/JsonApiResponse";
import type {ProjectData} from "~/types/ProjectData";
import {Swiper, SwiperSlide} from "swiper/vue";
import {Navigation} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import MarqueeAlpha from "~/components/MarqueeAlpha.vue";
import {userHasRole} from "~/utils/user";

const appUser = useAppUser();
const isReader = computed(() => !!appUser.value && userHasRole(appUser.value, "reader"));
const isAuthor = computed(() => !!appUser.value && userHasRole(appUser.value, "author"));

const {data: emotionsData} = await useEmotions().getEmotions();
const emotions = computed(() => emotionsData.value?.data ?? []);

const {data: randomProjectsResponse} = await useAsyncData("projects-random", () => {
  return $api<JsonApiResponse<ProjectData[]>>("/api/projects/random-selection");
});
const randomProjects = computed(() => randomProjectsResponse.value?.data ?? []);
</script>

<template>
  <UPage id="pagePage">
    <div id="pageBody" class="mt-0 bg-gray-100">

      <section class="m-0 border-b-2 border-primary bg-gradient-new pb-12 md:pb-0">
        <MarqueeAlpha></MarqueeAlpha>
        <div class="pt-12 md:pt-0">
          <div class="absolute z-0 w-full h-full hidden lg:block space-y-20 lg:space-y-25 h-short:space-y-24 overflow-hidden">
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10 invisible"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10 h-short:hidden"></div>
            <div class="w-full border-b border-dashed border-gray-300 -relative z-10 h-short:hidden"></div>
          </div>
          <div class="relative z-10 px-6 md:px-0 py:0 md:py-32.5 h-short:py-12">
            <div
                class="text-[3rem] md:text-[5.625rem] lg:text-[6.875rem] lg:h-short:text-[5.625rem] font-serif font-extrabold leading-none antialiased text-center mb-12 lg:mb-0">
              <p class="relative z-20 mb-0 h-short:-translate-y-4.25 lg:-translate-y-2.25 translate-x-0 md:-translate-x-15 md:-translate-x-30 md:max-w-1/2 m-auto">
                Lire.</p>
              <p class="relative z-20 mb-0 h-short:-translate-y-2.5 lg:-translate-y-4.5 translate-x-0 md:translate-x-12.5 md:translate-x-25 md:max-w-1/2 m-auto">
                Ressentir.</p>
              <p class="relative z-20 mb-0 h-short:-translate-y-0.75 lg:-translate-y-6.75 translate-x-0 md:-translate-x-12.5 md:-translate-x-25 md:max-w-1/2 m-auto">
                Soutenir.</p>
            </div>
            <div
                class="text-[1.25rem] md:text-[1.75rem] h-short:text-[1.5rem] font-extrabold mb-12 h-short:mb-16 translate-y-6 h-short:translate-y-13">
              <p class="block max-w-120 font-serif leading-none m-auto md:translate-x-16 lg:translate-x-36">Rejoins ton nouveau club de lecture.</p>
            </div>
            <div class="flex flex-col items-center gap-4 md:flex-row justify-center translate-y-8 h-short:translate-y-6">
              <UButton
                  class="w-full justify-center md:w-auto"
                  size="xl"
                  trailing-icon="i-heroicons-arrow-long-right"
                  color="primary"
                  variant="solid"
                  :to="isReader ? `/decouvrir`:`/lire`"
              >Commencer la lecture
              </UButton>
              <UButton
                  class="w-full justify-center md:w-auto"
                  size="xl"
                  trailing-icon="i-heroicons-arrow-long-right"
                  color="secondary"
                  variant="solid"
                  :to="isAuthor ? `/dashboard/projects`:`/ecrire`"
              >Je suis auteur•rice
              </UButton>
            </div>
          </div>
        </div>
      </section>

      <section class="m-0 py-8 md:py-16 lg:py-24 border-b-2 border-primary">
        <UContainer class="pb-8 md:pb-16 lg:pb-24">
          <div class="mb-2 md:mb-6">
            <div class="flex items-center justify-between">
              <h2 class="mb-2">
                <UIcon name="i-custom-trend" class="size-10"/>
                À lire en ce moment.
              </h2>
              <div class="flex gap-2">
                <UButton
                    icon="i-heroicons-arrow-long-left"
                    variant="link"
                    size="xl"
                    class="swiper-prev-random"
                />
                <UButton
                    icon="i-heroicons-arrow-long-right"
                    variant="link"
                    size="xl"
                    class="swiper-next-random"
                />
              </div>
            </div>
            <p class="text:sm md:text-xl lg:text-2xl font-medium">Les histoires recommandées par la communauté.</p>
          </div>
          <USeparator class="mb-6 md:mb-12" :ui="{border: 'border-primary'}"></USeparator>
          <div v-if="randomProjects.length > 0" class="mb-8">
            <Swiper
                :modules="[Navigation]"
                :navigation="{
          prevEl: '.swiper-prev-random',
          nextEl: '.swiper-next-random',
        }"
                :auto-height="false"
                :centered-slides="false"
                :slides-per-view="2"
                :space-between="16"
                :breakpoints="{
          768: { slidesPerView: 3, spaceBetween: 32 },
          1024: { slidesPerView: 3, spaceBetween: 64 },
        }"
                class="pb-4 swiper-stretch"
            >
              <SwiperSlide v-for="project in randomProjects" :key="project.uuid">
                <ProjectCard :project="project" responsive-layout="compact"/>
              </SwiperSlide>
            </Swiper>
          </div>
          <div class="flex justify-center">
            <UButton to="/decouvrir" class="w-full justify-center md:w-auto" color="secondary" variant="solid" size="xl"
                     trailing-icon="i-heroicons-arrow-long-right">
              Voir toutes les histoires
            </UButton>
          </div>
        </UContainer>

        <UContainer class="pt-8 md:pt-16 lg:pt-24">
          <h2 class="mb-2 md:mb-6">
            <UIcon name="i-custom-sparks" class="size-10"/>
            Une autre façon de vivre la fiction.
          </h2>
          <USeparator class="mb-6 md:mb-12" :ui="{border: 'border-primary'}"></USeparator>
          <div class="gap-8 grid md:grid-cols-2 lg:grid-cols-4">
            <UCard class="hover:bg-gradient-default hover:shadow-lg transition-colors">
              <UIcon name="i-custom-diamond" class="size-12 mb-8"/>
              <h3 class="text-h4 font-extrabold not-italic leading-none mb-4">Des talents à découvrir</h3>
              <p class="text-gray-800 leading-normal">Des auteurs talentueux existent au-delà de l'édition traditionnelle. Wreadia les met en
                lumière.</p>
            </UCard>
            <UCard class="hover:bg-gradient-default hover:shadow-lg transition-colors">
              <UIcon name="i-custom-biceps" class="size-12 mb-8"/>
              <h3 class="text-h4 font-extrabold not-italic leading-none mb-4">Qualité certifiée humaine</h3>
              <p class="text-gray-800 leading-normal">Certification sur l'honneur d'un contenu écrit sans IA. Déclaration horodatée à chaque
                publication.</p>
            </UCard>
            <UCard class="hover:bg-gradient-default hover:shadow-lg transition-colors">
              <UIcon name="i-custom-heart-sparks" class="size-12 mb-8"/>
              <h3 class="text-h4 font-extrabold not-italic leading-none mb-4">Un lien direct lecteur-auteur</h3>
              <p class="text-gray-800 leading-normal">Émotions par chapitre, tags BookTok, Scribs. Des échanges sincères qui font grandir les
                récits.</p>
            </UCard>
            <UCard class="hover:bg-gradient-default hover:shadow-lg transition-colors">
              <UIcon name="i-custom-piggy-bank" class="size-12 mb-8"/>
              <h3 class="text-h4 font-extrabold not-italic leading-none mb-4">70 à 80 % pour les auteurs</h3>
              <p class="text-gray-800 leading-normal">La grande majorité des revenus générés par vos lecteurs vous reviennent directement.</p>
            </UCard>
          </div>
        </UContainer>
      </section>

      <section class="m-0 py-8 md:py-16 lg:py-24 border-b-2 border-primary bg-gradient-default">
        <UContainer class="flex flex-col md:flex-row lg:px-24 gap-8 lg:gap-24">
          <div class="md:w-6/12 lg:w-5/12">
            <h2 class="mb-6">Ce que tu ressens compte autant que ce que tu lis.</h2>
            <p class="mb-8">
              <u>Ici, la lecture se vit</u>.<br> Tu réagis. Tu ressens. Tu nourris la conversation de tes émotions.
            </p>
            <div>
              <UButton color="neutral" variant="outline" size="lg" class="w-full justify-center md:w-auto" href="/lire">Moi aussi, je veux réagir !</UButton>
            </div>
          </div>
          <div class="md:w-6/12  lg:w-7/12 gap-1 flex flex-wrap">
            <NuxtLink
                v-for="emotion in emotions"
                :key="emotion.id"
                :to="{ path: '/decouvrir', query: { emotion: emotion.id }, hash: '#toutes-les-histoires' }"
                class="flex grow items-center gap-2 px-3.5 py-1.5 justify-center font-mono bg-white rounded-xs border-2 border-white hover:border-primary hover:bg-gray-200 transition-all duration-200"
            >
              <UIcon :name="emotion.icon" class="size-5"/>
              <span class="text-sm font-medium">{{ emotion.title }}</span>
            </NuxtLink>
          </div>
        </UContainer>
      </section>

      <section class="m-0 py-8 md:py-16 lg:py-24 border-b-2 border-primary bg-gradient-generic">
        <UContainer class="flex flex-col flex-col-reverse md:flex-row lg:px-24 gap-8 lg:gap-24">
          <div class="md:w-6/12">
            <img src="/comments-emotions.png" alt="Commentaires et émotions" class="w-full h-auto hidden lg:block">
            <img src="/comments-emotions-xs.png" alt="Commentaires et émotions" class="w-full h-auto lg:hidden">
          </div>
          <div class="md:w-6/12 flex flex-col justify-center">
            <h2 class="mb-6">Vos histoires méritent d’être lues</h2>
            <p class="mb-6">Publie tes récits, trouve ton audience, et échange avec de vrais lecteurs. Ici, ton travail ne reste pas invisible.</p>
            <div class="flex justify-center md:justify-start">
              <UButton color="neutral" variant="outline" size="lg" class="w-full justify-center md:w-auto" to="/ecrire">Je publie</UButton>
            </div>
          </div>
        </UContainer>
      </section>

      <section class="m-0 py-8 md:py-16 lg:py-24">
        <UContainer>
          <div class="text-center mb-12">
            <h2 class="mb-2">Rejoins l'aventure.</h2>
            <p class="text-2xl font-medium mb-4">Lecteur·ice ou auteur·ice, ta place est ici.</p>
            <p>Pendant l'alpha, Wreadia ouvre par étapes.</p>
            <p>Les lectrices et lecteurs s'inscrivent pour rejoindre le club, les autrices et auteurs candidatent pour publier.</p>
            <p>Tout se passe via les pages ci-dessous.</p>
          </div>
          <div class="flex flex-col md:flex-row lg:px-24 gap-8 lg:gap-16">
            <UCard class="md:w-1/2 hover:bg-gradient-generic hover:shadow-lg transition-colors">
              <h3 class="text-h4 mb-3">
                <UIcon name="i-custom-book-pile" class="size-6 translate-y-1"/>
                Lecteur·ice
              </h3>
              <p>Inscris-toi pour rejoindre le club et commencer à lire les mondes de Wreadia.</p>
              <div class="mt-4">
                <UButton
                    color="secondary"
                    variant="solid"
                    to="/lire"
                    block
                >
                  Je rejoins le club
                </UButton>
              </div>
            </UCard>
            <UCard class="md:w-1/2 hover:bg-gradient-generic hover:shadow-lg transition-colors">
              <h3 class="text-h4 mb-3">
                <UIcon name="i-custom-paper-pencil" class="size-6"/>
                Auteur·ice
              </h3>
              <p>Candidate pour publier tes histoires sur Wreadia. Places fondatrices limitées.</p>
              <div class="mt-4">
                <UButton
                    color="secondary"
                    variant="solid"
                    to="/ecrire"
                    block
                >
                  Je m'inscris
                </UButton>
              </div>
            </UCard>
          </div>
        </UContainer>
      </section>

    </div>
  </UPage>
</template>
